import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, LoaderCircle, Save } from "lucide-react";
import { useState } from "react";
import { type UseFormSetError, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";

// PersonaOpcion es una entrada del selector de persona (id_persona + etiqueta).
export type PersonaOpcion = { id: number; label: string };

// Espejo de la validación de servidor (obras/dto.go): persona obligatoria, fechas
// AAAA-MM-DD y caducidad ≥ fecha de autorización, textos acotados.
const autorizacionSchema = z
  .object({
    id_persona: z.coerce.number().int().positive("Selecciona una persona"),
    autorizado_loto: z.boolean(),
    vigente: z.boolean(),
    fecha_autorizacion: z.string(),
    fecha_caducidad: z.string(),
    emitida_por: z.string().trim().max(120, "Máximo 120 caracteres"),
    alcance: z.string().trim().max(500, "Máximo 500 caracteres"),
    observaciones: z.string().trim().max(500, "Máximo 500 caracteres"),
  })
  .superRefine((val, ctx) => {
    if (
      val.fecha_autorizacion !== "" &&
      val.fecha_caducidad !== "" &&
      val.fecha_caducidad < val.fecha_autorizacion
    ) {
      ctx.addIssue({
        path: ["fecha_caducidad"],
        code: z.ZodIssueCode.custom,
        message: "La caducidad no puede ser anterior a la fecha de autorización",
      });
    }
  });

// El esquema coacciona id_persona (z.coerce.number): el tipo de entrada difiere del de
// salida ya validado. Con @hookform/resolvers v5 hay que tipar useForm con ambos.
type AutorizacionFormInput = z.input<typeof autorizacionSchema>;
export type AutorizacionFormValues = z.output<typeof autorizacionSchema>;

function aplicarError(
  err: unknown,
  setError: UseFormSetError<AutorizacionFormValues>,
  setFormError: (msg: string) => void,
): void {
  if (err instanceof ApiError) {
    if (err.fields) {
      const campos = [
        "id_persona",
        "fecha_autorizacion",
        "fecha_caducidad",
        "emitida_por",
        "alcance",
        "observaciones",
      ] as const;
      let mapped = false;
      for (const campo of campos) {
        const msg = err.fields[campo];
        if (msg) {
          setError(campo, { message: msg });
          mapped = true;
        }
      }
      if (mapped) {
        return;
      }
    }
    setFormError(err.message || "No se pudo guardar la autorización.");
    return;
  }
  setFormError("No se pudo guardar la autorización. Comprueba tu conexión.");
}

/**
 * AutorizacionForm habilita/actualiza la autorización LOTO de una persona en la obra
 * (react-hook-form + zod). En alta ofrece un selector de persona poblado con los
 * MIEMBROS de la obra (#161: el GET de miembros es accesible a cualquier gestor,
 * también CUALIFICADO — la autorización LOTO se concede a gente de la obra); en
 * edición la persona es fija (nombre mostrado). Mapea los errores por campo del
 * backend. Sigue el patrón del sub-formulario de autorización de la antigua gestión
 * de usuarios (#34).
 */
export function AutorizacionForm({
  mode,
  personas,
  personaFija,
  defaultValues,
  pending,
  onSubmit,
  onCancel,
}: {
  mode: "crear" | "editar";
  personas: PersonaOpcion[];
  personaFija?: string;
  defaultValues: AutorizacionFormValues;
  pending: boolean;
  onSubmit: (values: AutorizacionFormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<AutorizacionFormInput, unknown, AutorizacionFormValues>({
    resolver: zodResolver(autorizacionSchema),
    defaultValues,
  });

  const submit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      aplicarError(err, setError, setFormError);
    }
  });

  return (
    <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
      {formError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{formError}</span>
        </Alert>
      ) : null}

      {mode === "editar" ? (
        <div className="flex flex-col gap-1.5">
          <span className="font-medium text-slate-700 text-sm">Persona</span>
          <p className="text-base text-slate-900">{personaFija}</p>
          {/* La persona no cambia en edición: viaja en el payload como campo oculto. */}
          <input type="hidden" {...register("id_persona")} />
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {personas.length === 0 ? (
            <Alert variant="info">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>La obra aún no tiene miembros: añade primero al miembro a autorizar.</span>
            </Alert>
          ) : null}
          <Label htmlFor="id_persona">Persona</Label>
          <Select
            id="id_persona"
            aria-invalid={errors.id_persona ? true : undefined}
            {...register("id_persona")}
          >
            <option value={0}>Selecciona una persona…</option>
            {personas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </Select>
          <FieldError>{errors.id_persona?.message}</FieldError>
        </div>
      )}

      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
          {...register("autorizado_loto")}
        />
        <span className="text-base text-slate-800">Autorizado para LOTO</span>
      </label>
      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
          {...register("vigente")}
        />
        <span className="text-base text-slate-800">Autorización vigente</span>
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="fecha_autorizacion">Fecha de autorización</Label>
          <Input
            id="fecha_autorizacion"
            type="date"
            aria-invalid={errors.fecha_autorizacion ? true : undefined}
            {...register("fecha_autorizacion")}
          />
          <FieldError>{errors.fecha_autorizacion?.message}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="fecha_caducidad">Fecha de caducidad</Label>
          <Input
            id="fecha_caducidad"
            type="date"
            aria-invalid={errors.fecha_caducidad ? true : undefined}
            {...register("fecha_caducidad")}
          />
          <FieldError>{errors.fecha_caducidad?.message}</FieldError>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="emitida_por">Emitida por (opcional)</Label>
        <Input
          id="emitida_por"
          autoComplete="off"
          aria-invalid={errors.emitida_por ? true : undefined}
          {...register("emitida_por")}
        />
        <FieldError>{errors.emitida_por?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="alcance">Alcance (opcional)</Label>
        <Input
          id="alcance"
          autoComplete="off"
          aria-invalid={errors.alcance ? true : undefined}
          {...register("alcance")}
        />
        <FieldError>{errors.alcance?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="observaciones">Observaciones (opcional)</Label>
        <Textarea
          id="observaciones"
          aria-invalid={errors.observaciones ? true : undefined}
          {...register("observaciones")}
        />
        <FieldError>{errors.observaciones?.message}</FieldError>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={pending}>
          {pending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-5 w-5" aria-hidden="true" />
          )}
          Guardar autorización
        </Button>
      </div>
    </form>
  );
}
